/**
 * Particle and tyre-mark system.
 *
 * Particles are drawn from a fixed-size pool with zero allocation per frame.
 * Tyre marks are painted into a track-sized offscreen canvas and faded in
 * slices, which is far cheaper than keeping a list of thousands of segments.
 */

import { clamp } from '@shared/math.js';

export type ParticleKind = 'smoke' | 'dust' | 'spark' | 'debris' | 'boost' | 'splash';

interface Particle {
  active: boolean;
  kind: ParticleKind;
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  size: number;
  growth: number;
  color: string;
  drag: number;
}

const MAX_PARTICLES = 900;

export class ParticleSystem {
  private particles: Particle[] = [];
  private next = 0;
  /** Reduced-motion / low-quality mode cuts the count in half. */
  quality = 1;

  constructor() {
    for (let i = 0; i < MAX_PARTICLES; i++) {
      this.particles.push({
        active: false, kind: 'smoke', x: 0, y: 0, vx: 0, vy: 0,
        life: 0, maxLife: 1, size: 2, growth: 0, color: '#fff', drag: 0.9,
      });
    }
  }

  clear(): void {
    for (const p of this.particles) p.active = false;
  }

  get activeCount(): number {
    let n = 0;
    for (const p of this.particles) if (p.active) n++;
    return n;
  }

  private spawn(): Particle {
    // Ring buffer: the oldest particle is recycled once the pool is exhausted,
    // which degrades gracefully instead of allocating under load.
    for (let i = 0; i < MAX_PARTICLES; i++) {
      const p = this.particles[this.next]!;
      this.next = (this.next + 1) % MAX_PARTICLES;
      if (!p.active) return p;
    }
    const p = this.particles[this.next]!;
    this.next = (this.next + 1) % MAX_PARTICLES;
    return p;
  }

  emit(
    kind: ParticleKind,
    x: number,
    y: number,
    vx: number,
    vy: number,
    life: number,
    size: number,
    color: string,
    growth = 0,
    drag = 0.9,
  ): void {
    if (this.quality <= 0) return;
    const p = this.spawn();
    p.active = true;
    p.kind = kind;
    p.x = x;
    p.y = y;
    p.vx = vx;
    p.vy = vy;
    p.life = life;
    p.maxLife = life;
    p.size = size;
    p.growth = growth;
    p.color = color;
    p.drag = drag;
  }

  /** Tyre smoke / dust, scaled by how much the tyre is slipping. */
  tyreSmoke(x: number, y: number, vx: number, vy: number, intensity: number, color: string, count = 2): void {
    const n = Math.max(1, Math.round(count * intensity * this.quality));
    for (let i = 0; i < n; i++) {
      const spread = 26;
      this.emit(
        'smoke',
        x + (Math.random() - 0.5) * 6,
        y + (Math.random() - 0.5) * 6,
        vx * 0.22 + (Math.random() - 0.5) * spread,
        vy * 0.22 + (Math.random() - 0.5) * spread,
        0.35 + Math.random() * 0.45 * (0.5 + intensity),
        3 + intensity * 5,
        color,
        12 * intensity + 4,
        0.86,
      );
    }
  }

  sparks(x: number, y: number, nx: number, ny: number, strength: number): void {
    const n = Math.min(20, Math.round(3 + strength / 26));
    for (let i = 0; i < n; i++) {
      const spread = (Math.random() - 0.5) * 2.2;
      const ang = Math.atan2(ny, nx) + spread;
      const speed = 60 + Math.random() * strength * 0.9;
      this.emit(
        'spark',
        x,
        y,
        Math.cos(ang) * speed,
        Math.sin(ang) * speed,
        0.18 + Math.random() * 0.3,
        1 + Math.random() * 1.8,
        Math.random() < 0.4 ? '#fff2b0' : '#ffb347',
        0,
        0.9,
      );
    }
  }

  boostTrail(x: number, y: number, vx: number, vy: number, color: string): void {
    const n = Math.max(1, Math.round(3 * this.quality));
    for (let i = 0; i < n; i++) {
      this.emit(
        'boost',
        x + (Math.random() - 0.5) * 5,
        y + (Math.random() - 0.5) * 5,
        vx * 0.1 + (Math.random() - 0.5) * 20,
        vy * 0.1 + (Math.random() - 0.5) * 20,
        0.2 + Math.random() * 0.2,
        2 + Math.random() * 3,
        color,
        6,
        0.88,
      );
    }
  }

  debris(x: number, y: number, vx: number, vy: number, color: string, count = 6): void {
    for (let i = 0; i < count * this.quality; i++) {
      const ang = Math.random() * Math.PI * 2;
      const speed = 40 + Math.random() * 90;
      this.emit('debris', x, y, vx * 0.2 + Math.cos(ang) * speed, vy * 0.2 + Math.sin(ang) * speed, 0.4 + Math.random() * 0.4, 1 + Math.random() * 2, color, 0, 0.9);
    }
  }

  update(dt: number): void {
    for (const p of this.particles) {
      if (!p.active) continue;
      p.life -= dt;
      if (p.life <= 0) {
        p.active = false;
        continue;
      }
      const dragFactor = Math.pow(p.drag, dt * 60);
      p.vx *= dragFactor;
      p.vy *= dragFactor;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      if (p.growth !== 0) p.size += p.growth * dt;
    }
  }

  /** Draws every particle. `ctx` is expected to be in world space. */
  draw(ctx: CanvasRenderingContext2D): void {
    for (const p of this.particles) {
      if (!p.active) continue;
      const t = clamp(p.life / p.maxLife, 0, 1);
      ctx.globalAlpha = p.kind === 'spark' ? t : t * 0.72;
      ctx.fillStyle = p.color;
      if (p.kind === 'spark') {
        // Sparks read better as short streaks than as dots.
        const len = Math.min(9, Math.hypot(p.vx, p.vy) * 0.03);
        const ang = Math.atan2(p.vy, p.vx);
        ctx.fillRect(p.x, p.y, Math.cos(ang) * len + 1.4, Math.sin(ang) * len + 1.4);
      } else {
        const s = Math.max(1, Math.round(p.size));
        ctx.fillRect(Math.round(p.x - s / 2), Math.round(p.y - s / 2), s, s);
      }
    }
    ctx.globalAlpha = 1;
  }
}

/**
 * Tyre marks painted onto a track-sized canvas.
 *
 * Marks are drawn as short segments; every so often the whole layer is faded by
 * a tiny amount so a long session does not turn the track into one black smear.
 */
export class SkidLayer {
  readonly canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private fadeAccumulator = 0;
  private markCount = 0;

  constructor(width: number, height: number) {
    this.canvas = document.createElement('canvas');
    this.canvas.width = Math.ceil(width);
    this.canvas.height = Math.ceil(height);
    const ctx = this.canvas.getContext('2d');
    if (!ctx) throw new Error('2D canvas context unavailable');
    this.ctx = ctx;
  }

  clear(): void {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.markCount = 0;
  }

  mark(x1: number, y1: number, x2: number, y2: number, width: number, alpha: number, color = '#181a1c'): void {
    if (alpha <= 0.02) return;
    this.ctx.globalAlpha = clamp(alpha, 0, 0.75);
    this.ctx.strokeStyle = color;
    this.ctx.lineWidth = width;
    this.ctx.lineCap = 'butt';
    this.ctx.beginPath();
    this.ctx.moveTo(x1, y1);
    this.ctx.lineTo(x2, y2);
    this.ctx.stroke();
    this.ctx.globalAlpha = 1;
    this.markCount++;
  }

  /** Call once per frame; occasionally lightens the layer to stop saturation. */
  update(dt: number): void {
    this.fadeAccumulator += dt;
    if (this.fadeAccumulator < 6 || this.markCount < 4000) return;
    this.fadeAccumulator = 0;
    this.markCount = 0;
    this.ctx.globalCompositeOperation = 'destination-out';
    this.ctx.fillStyle = 'rgba(0,0,0,0.06)';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    this.ctx.globalCompositeOperation = 'source-over';
  }
}
